import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ArchiveCategory, ArchiveItem, ArchiveVisibility, DigitisationRequest, Page, ReadingRoomRequest } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge, Tabs } from '../../ui/components';

const CATEGORIES: ArchiveCategory[] = ['PHOTOGRAPH', 'DOCUMENT', 'AUDIO', 'VIDEO', 'MAP', 'MANUSCRIPT', 'OBJECT', 'NEWSPAPER', 'OTHER'];

const TABS = [
  { key: 'catalogue', label: 'Archive catalogue' },
  { key: 'digitisation', label: 'Digitisation queue' },
  { key: 'readingroom', label: 'Reading room' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function ArchivesPage() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('ARCHIVIST') ?? false;
  const [tab, setTab] = useState<TabKey>('catalogue');

  return (
    <div>
      <PageHeader
        title="National Archives"
        subtitle="Catalogue, digitisation queue, and reading-room bookings — archives.ts."
      />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Signed in without the Archivist role — showing the public catalogue only. Cataloguing, digitisation, and reading-room admin require ARCHIVIST." />
        </div>
      ) : null}
      {tab === 'catalogue' ? <CatalogueTab canManage={canManage} /> : null}
      {tab === 'digitisation' ? <DigitisationTab canManage={canManage} /> : null}
      {tab === 'readingroom' ? <ReadingRoomTab canManage={canManage} /> : null}
    </div>
  );
}

// ── Catalogue ──────────────────────────────────────────────────────────

function CatalogueTab({ canManage }: { canManage: boolean }) {
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = new URLSearchParams({ limit: '100' });
  if (category) query.set('category', category);
  if (q) query.set('q', q);
  const itemsQ = useApiQuery<Page<ArchiveItem>>(`/api/v1/m30/archives?${query.toString()}`);
  const items = itemsQ.data?.items ?? [];
  const selected = items.find((i) => i.id === selectedId) ?? null;

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-3 flex flex-wrap gap-2">
          <input className="field-input max-w-xs" placeholder="Search title or description…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {canManage ? (
            <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
              + Catalogue item
            </button>
          ) : null}
        </div>
        {itemsQ.error ? <ErrorNote message={itemsQ.error} /> : null}
        <div className="card overflow-x-auto">
          {itemsQ.loading ? (
            <Spinner />
          ) : items.length === 0 ? (
            <EmptyState title="No archive items found" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Title</th>
                  <th className="table-head">Category</th>
                  <th className="table-head">Visibility</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === i.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(i.id)}>
                    <td className="table-cell">
                      <div className="font-medium">{i.title}</div>
                      <div className="text-[11px] text-slate-400">{i.accessionNo ?? '—'}</div>
                    </td>
                    <td className="table-cell text-xs text-slate-500">{i.category}</td>
                    <td className="table-cell">
                      <StatusBadge status={i.visibility} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="lg:col-span-2">
        {selected ? (
          <ArchiveItemDetail key={selected.id} item={selected} canManage={canManage} onChanged={itemsQ.reload} />
        ) : (
          <SectionCard>
            <EmptyState title="Select an archive item" />
          </SectionCard>
        )}
      </div>
      {showCreate ? (
        <CreateArchiveItemModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            itemsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ArchiveItemDetail({ item, canManage, onChanged }: { item: ArchiveItem; canManage: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  return (
    <SectionCard
      title={item.title}
      actions={
        canManage ? (
          <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Close' : 'Edit'}
          </button>
        ) : undefined
      }
    >
      {editing ? (
        <EditArchiveItemForm
          item={item}
          onDone={() => {
            setEditing(false);
            onChanged();
          }}
        />
      ) : (
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">Category</dt>
            <dd className="font-medium text-slate-800">{item.category}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Origin</dt>
            <dd className="font-medium text-slate-800">{item.creatorOrOrigin ?? '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Date created</dt>
            <dd className="font-medium text-slate-800">{item.dateCreated ?? '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">District</dt>
            <dd className="font-medium text-slate-800">{item.district ?? '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Visibility</dt>
            <dd>
              <StatusBadge status={item.visibility} />
            </dd>
          </div>
          {item.digitisedFileRef ? (
            <div className="flex justify-between">
              <dt className="text-slate-500">Digitised file</dt>
              <dd className="text-right text-xs font-medium text-slate-800">{item.digitisedFileRef}</dd>
            </div>
          ) : null}
          {item.iiifManifestUrl ? (
            <div>
              <dt className="text-slate-500">IIIF manifest</dt>
              <dd className="mt-0.5">
                <a className="text-xs text-brand-600 underline" href={item.iiifManifestUrl} target="_blank" rel="noreferrer">
                  {item.iiifManifestUrl}
                </a>
              </dd>
            </div>
          ) : null}
          {item.description ? <p className="pt-1 text-xs text-slate-600">{item.description}</p> : null}
        </dl>
      )}
    </SectionCard>
  );
}

function EditArchiveItemForm({ item, onDone }: { item: ArchiveItem; onDone: () => void }) {
  const [description, setDescription] = useState(item.description ?? '');
  const [visibility, setVisibility] = useState<ArchiveVisibility>(item.visibility);
  const [digitisedFileRef, setDigitisedFileRef] = useState(item.digitisedFileRef ?? '');
  const [iiifManifestUrl, setIiifManifestUrl] = useState(item.iiifManifestUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m30/archives/${item.id}`, {
        description: description || undefined,
        visibility,
        digitisedFileRef: digitisedFileRef || undefined,
        iiifManifestUrl: iiifManifestUrl || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Description">
        <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Visibility">
        <select className="field-input" value={visibility} onChange={(e) => setVisibility(e.target.value as ArchiveVisibility)}>
          {(['PUBLIC', 'RESTRICTED', 'DARK'] as const).map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Digitised file reference">
        <input className="field-input" value={digitisedFileRef} onChange={(e) => setDigitisedFileRef(e.target.value)} />
      </Field>
      <Field label="IIIF manifest URL">
        <input className="field-input" value={iiifManifestUrl} onChange={(e) => setIiifManifestUrl(e.target.value)} />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}

function CreateArchiveItemModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<ArchiveCategory>('PHOTOGRAPH');
  const [description, setDescription] = useState('');
  const [creatorOrOrigin, setCreatorOrOrigin] = useState('');
  const [dateCreated, setDateCreated] = useState('');
  const [accessionNo, setAccessionNo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/archives', {
        title,
        category,
        description: description || undefined,
        creatorOrOrigin: creatorOrOrigin || undefined,
        dateCreated: dateCreated || undefined,
        accessionNo: accessionNo || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to catalogue item');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Catalogue a new archive item" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as ArchiveCategory)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Accession no. (optional)">
            <input className="field-input" value={accessionNo} onChange={(e) => setAccessionNo(e.target.value)} placeholder="e.g. NA-PH-2026-0001" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Creator / origin">
            <input className="field-input" value={creatorOrOrigin} onChange={(e) => setCreatorOrOrigin(e.target.value)} />
          </Field>
          <Field label="Date created">
            <input className="field-input" value={dateCreated} onChange={(e) => setDateCreated(e.target.value)} placeholder="e.g. c. 1970s" />
          </Field>
        </div>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 2}>
            {busy ? 'Cataloguing…' : 'Catalogue item'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Digitisation queue ─────────────────────────────────────────────────

function DigitisationTab({ canManage }: { canManage: boolean }) {
  const [status, setStatus] = useState('');
  const reqQ = useApiQuery<Page<DigitisationRequest>>(canManage ? `/api/v1/m30/digitisation-requests?limit=100${status ? `&status=${status}` : ''}` : null);
  const archivesQ = useApiQuery<Page<ArchiveItem>>(canManage ? '/api/v1/m30/archives?limit=200' : null);

  if (!canManage) return <ErrorNote message="The digitisation queue requires the Archivist role." />;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(['QUEUED', 'IN_PROGRESS', 'COMPLETED', 'DECLINED'] as const).map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      {reqQ.error ? <ErrorNote message={reqQ.error} /> : null}
      <div className="card overflow-x-auto">
        {reqQ.loading ? (
          <Spinner />
        ) : (reqQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No digitisation requests" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Requester</th>
                <th className="table-head">Item</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {reqQ.data!.items.map((r) => (
                <DigitisationRow key={r.id} request={r} archiveItems={archivesQ.data?.items ?? []} onChanged={reqQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DigitisationRow({ request, archiveItems, onChanged }: { request: DigitisationRequest; archiveItems: ArchiveItem[]; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultId, setResultId] = useState('');

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/digitisation-requests/${request.id}/start`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    const reason = prompt('Decline reason:');
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/digitisation-requests/${request.id}/decline`, { reason });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!resultId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/digitisation-requests/${request.id}/complete`, { resultingArchiveItemId: resultId });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50 align-top">
      <td className="table-cell">
        {request.requesterName}
        {request.requesterId ? <div className="text-[11px] text-slate-400">{request.requesterId}</div> : null}
      </td>
      <td className="table-cell text-xs text-slate-500">
        {request.itemDescription}
        {request.provenance ? <div className="mt-0.5 text-slate-400">{request.provenance}</div> : null}
      </td>
      <td className="table-cell">
        <StatusBadge status={request.status} />
        {error ? <div className="mt-1 text-[11px] text-rose-600">{error}</div> : null}
      </td>
      <td className="table-cell text-right">
        {request.status === 'QUEUED' ? (
          <div className="flex justify-end gap-1.5">
            <button className="btn-secondary !px-2 !py-1 text-xs" onClick={start} disabled={busy}>
              Start
            </button>
            <button className="btn-danger !px-2 !py-1 text-xs" onClick={decline} disabled={busy}>
              Decline
            </button>
          </div>
        ) : request.status === 'IN_PROGRESS' ? (
          <div className="flex flex-col items-end gap-1.5">
            <select className="field-input !w-auto !py-1 text-xs" value={resultId} onChange={(e) => setResultId(e.target.value)}>
              <option value="">Link resulting item…</option>
              {archiveItems.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title}
                </option>
              ))}
            </select>
            <div className="flex gap-1.5">
              <button className="btn-success !px-2 !py-1 text-xs" onClick={complete} disabled={busy || !resultId}>
                Complete
              </button>
              <button className="btn-danger !px-2 !py-1 text-xs" onClick={decline} disabled={busy}>
                Decline
              </button>
            </div>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

// ── Reading room ───────────────────────────────────────────────────────

function ReadingRoomTab({ canManage }: { canManage: boolean }) {
  const [status, setStatus] = useState('');
  const reqQ = useApiQuery<Page<ReadingRoomRequest>>(canManage ? `/api/v1/m30/reading-room-requests?limit=100${status ? `&status=${status}` : ''}` : null);

  if (!canManage) return <ErrorNote message="Reading-room bookings require the Archivist role." />;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(['REQUESTED', 'APPROVED', 'DECLINED', 'COMPLETED', 'CANCELLED'] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {reqQ.error ? <ErrorNote message={reqQ.error} /> : null}
      <div className="card overflow-x-auto">
        {reqQ.loading ? (
          <Spinner />
        ) : (reqQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No reading-room requests" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Requester</th>
                <th className="table-head">Purpose</th>
                <th className="table-head">Date</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {reqQ.data!.items.map((r) => (
                <ReadingRoomRow key={r.id} request={r} onChanged={reqQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ReadingRoomRow({ request, onChanged }: { request: ReadingRoomRequest; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: 'approve' | 'complete') => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/reading-room-requests/${request.id}/${action}`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    const reason = prompt('Decline reason:');
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/reading-room-requests/${request.id}/decline`, { reason });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell font-medium">{request.requesterName}</td>
      <td className="table-cell text-xs text-slate-500">{request.purpose}</td>
      <td className="table-cell text-xs tabular-nums">{formatDate(request.requestedDate)}</td>
      <td className="table-cell">
        <StatusBadge status={request.status} />
        {error ? <div className="mt-1 text-[11px] text-rose-600">{error}</div> : null}
      </td>
      <td className="table-cell text-right">
        {request.status === 'REQUESTED' ? (
          <div className="flex justify-end gap-1.5">
            <button className="btn-success !px-2 !py-1 text-xs" onClick={() => act('approve')} disabled={busy}>
              Approve
            </button>
            <button className="btn-danger !px-2 !py-1 text-xs" onClick={decline} disabled={busy}>
              Decline
            </button>
          </div>
        ) : request.status === 'APPROVED' ? (
          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => act('complete')} disabled={busy}>
            Mark completed
          </button>
        ) : null}
      </td>
    </tr>
  );
}
